import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(Math.round((n || 0) * 1000) / 1000);

function AdminInventoryImport({ user, onLogout }) {
  const { lang } = useParams();
  const currentLang = lang || 'ru';
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setCommitted(null);
  };

  const send = async (mode) => {
    if (!file) { toast.error('Выберите .xlsx файл'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/inventory-import/${mode}`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (mode === 'preview') { setPreview(data); setCommitted(null); }
      else { setCommitted(data); toast.success('Импорт выполнен'); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка импорта');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-inventory-import">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/${currentLang}/admin/production`)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Импорт из Excel</h1>
            <p className="text-sm text-gray-500">Разовый импорт ингредиентов (цена, min/max) и рецептур из файла учёта затрат</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <Card><CardContent className="p-5 space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <label className="flex-1 w-full">
              <input type="file" accept=".xlsx,.xlsm" onChange={onPick} className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-medium hover:file:bg-emerald-100" data-testid="file-input" />
            </label>
            <div className="flex gap-2">
              <Button onClick={() => send('preview')} disabled={!file || loading} variant="outline" data-testid="preview-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Предпросмотр
              </Button>
              <Button onClick={() => send('commit')} disabled={!preview || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="commit-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" />Применить
              </Button>
            </div>
          </div>
          {file && <p className="text-xs text-gray-500" data-testid="selected-file">Выбран: {file.name}</p>}
        </CardContent></Card>

        {committed && (
          <Card><CardContent className="p-5" data-testid="commit-result">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3"><CheckCircle2 className="w-5 h-5" /> Импорт завершён</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat label="Ингредиентов создано" value={committed.ingredients_created} />
              <Stat label="Ингредиентов обновлено" value={committed.ingredients_updated} />
              <Stat label="Рецептов создано" value={committed.recipes_created} />
              <Stat label="Рецептов обновлено" value={committed.recipes_updated} />
            </div>
          </CardContent></Card>
        )}

        {preview && (
          <div className="space-y-4" data-testid="preview-result">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat card label="Ингредиентов в файле" value={preview.ingredients_total} />
              <Stat card label="Новых ингредиентов" value={preview.ingredients_new.length} />
              <Stat card label="Будет обновлено" value={preview.ingredients_update} />
              <Stat card label="С лимитами min/max" value={preview.ingredients_with_minmax} />
            </div>

            <Card><CardContent className="p-5">
              <div className="font-semibold mb-2 flex items-center gap-2">Рецептуры <Badge className="bg-gray-100 text-gray-700">{preview.recipes_total}</Badge></div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-emerald-700 font-medium mb-1">Сопоставлены с товаром ({preview.products_matched.length})</div>
                  <ul className="space-y-0.5 text-gray-600 max-h-48 overflow-y-auto">
                    {preview.products_matched.map((p) => <li key={p}>• {p}</li>)}
                    {preview.products_matched.length === 0 && <li className="text-gray-400">—</li>}
                  </ul>
                </div>
                <div>
                  <div className="text-amber-700 font-medium mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Без товара в каталоге ({preview.products_unmatched.length})</div>
                  <ul className="space-y-0.5 text-gray-600 max-h-48 overflow-y-auto">
                    {preview.products_unmatched.map((p) => <li key={p}>• {p}</li>)}
                    {preview.products_unmatched.length === 0 && <li className="text-gray-400">—</li>}
                  </ul>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Рецепты без сопоставленного товара всё равно сохранятся по названию и могут быть привязаны позже.</p>
            </CardContent></Card>

            {preview.ingredients_new.length > 0 && (
              <Card><CardContent className="p-5">
                <div className="font-semibold mb-2">Новые ингредиенты ({preview.ingredients_new.length})</div>
                <div className="flex flex-wrap gap-1.5">
                  {preview.ingredients_new.map((n) => <Badge key={n} variant="outline">{n}</Badge>)}
                </div>
              </CardContent></Card>
            )}

            {preview.sample?.length > 0 && (
              <Card><CardContent className="p-5">
                <div className="font-semibold mb-2">Пример рецепта: {preview.sample[0].name}</div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600"><tr><th className="text-left p-2">Ингредиент</th><th className="text-right p-2">Норма на 1 шт</th><th className="text-left p-2">Ед.</th></tr></thead>
                  <tbody>
                    {preview.sample[0].inputs.map((inp, i) => (
                      <tr key={i} className="border-t"><td className="p-2">{inp.name}</td><td className="p-2 text-right">{fmt(inp.quantity)}</td><td className="p-2">{inp.unit}</td></tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, card }) => (
  card
    ? <Card><CardContent className="p-4"><div className="text-2xl font-bold">{value}</div><div className="text-xs text-gray-500 mt-1">{label}</div></CardContent></Card>
    : <div><div className="text-2xl font-bold">{value}</div><div className="text-xs text-gray-500 mt-1">{label}</div></div>
);

export default AdminInventoryImport;
